import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

import StrategyBriefHubStepLayout from '@/brief-hub/common/ui/layout/StrategyBriefHubStepLayout.vue';
    import {computed, inject, ref, watch} from 'vue';
    import {DI} from '@/brief-hub/config/DependencyInjection';
    import {FreelancerSkillsService} from '@/common/api/FreelancerSkillsService';
    import {useGlobalProperties} from '@malt/vue3-use';
    import {useSkills} from '@/common/composable/useSkills';
    import {Skill} from '@/common/types/Skill';
    import {useBriefStrategyStore} from '@/brief-hub/stores/briefStrategy.store';
    import {StrategyDraftState, LanguageResource} from '@/common/api/api-raw-types';
    import {getPageLanguageCode} from '@malt/dom';
    import {LanguagesService} from '@/brief-hub/brief/LanguagesService';
    import LanguageField from '@/brief-hub/strategy/deposit/freelancer/LanguageField.vue';
    import {NavigationStrategyService} from '@/brief-hub/brief/NavigationStrategyService';
    import SkillsField from '@/brief-hub/strategy/deposit/freelancer/SkillsField.vue';
    import {useBriefAmplitudeEvent} from '@/common/composable/useBriefAmplitudeEvent';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';

    const MAX_SKILLS = 6;
    
export default /*@__PURE__*/_defineComponent({
  __name: 'StrategyBriefFreelancerSkills',
  setup(__props) {

    const pageLanguageCode = getPageLanguageCode();

    const {$msg, $features} = useGlobalProperties();
    const briefStrategyStore = useBriefStrategyStore();
    const querySkill = ref<string>('');
    const navigationStrategyService: NavigationStrategyService | undefined = inject(DI.navigationStrategyService);
    const freelancerSkillsService: FreelancerSkillsService | undefined = inject(DI.freelancerSkillsService);
    const languagesService: LanguagesService | undefined = inject(DI.languagesService);
    const selectedSkills = ref<string[]>(briefStrategyStore.briefStrategyFreelancerSkills.freelancerSkills || []);
    const {skills, suggestedSkills, checkSuggestedSkills} = useSkills(
        selectedSkills.value.map((s) => new Skill(s)),
        freelancerSkillsService!,
    );
    const jobTitle = ref<string>(briefStrategyStore.briefStrategyFreelancerSkills.jobTitle);
    const category = ref<string>(briefStrategyStore.briefStrategyFreelancerSkills.category);
    const languages = ref<LanguageResource[]>(briefStrategyStore.briefStrategyFreelancerSkills.languages || []);
    const showSkipLink = computed<boolean>(() => !skills.value.length && !languages.value.length);
    const {triggerBriefFunnelStepEnteredAmplitudeEvent} = useBriefAmplitudeEvent();

    function onFormChange() {
        checkSuggestedSkills();

        if (skills.value.length <= MAX_SKILLS) {
            briefStrategyStore.updatePayload({
                type: 'StrategyFreelancerSkillsBriefRequest',
                payload: {
                    freelancerSkills: skills.value.map((s) => s.name),
                    languageCodes: languages.value.map((language) => language.code),
                },
            });
        } else {
            briefStrategyStore.resetPayload();
        }
    }

    function init() {
        setPageLanguageSkillForNewBrief();
        triggerBriefFunnelStepEnteredAmplitudeEvent('skills', 4);
    }
    function languageValueChanged(autoCompletedValue: LanguageResource): void {
        if (!autoCompletedValue) {
            return;
        }

        const alreadyPresent = languages.value.find((language) => language.code === autoCompletedValue.code);

        if (!alreadyPresent) {
            languages.value.push(autoCompletedValue);
        }
    }

    function handleUpdateSkill(newSkills) {
        skills.value = newSkills;
    }

    function setPageLanguageSkillForNewBrief() {
        const isFirstTimeSkillsScreenDisplayed =
            !navigationStrategyService!.isRequiredDraftStateCompleted(
                briefStrategyStore.brief!.draftState,
                StrategyDraftState.FREELANCER_SKILLS_SECTION_COMPLETED,
            ) && skills.value.length === 0;
        if (isFirstTimeSkillsScreenDisplayed && pageLanguageCode) {
            // @ts-ignore
            const language = new Intl.DisplayNames([pageLanguageCode], {type: 'language'}).of(pageLanguageCode);
            if (language) {
                languageValueChanged({code: pageLanguageCode, label: language});
            }
        }
    }

    watch(querySkill, async (newQuery) => {
        if (freelancerSkillsService) {
            const [data, error] = await freelancerSkillsService.search(newQuery);
            if (error || !data) {
                pushVJoySnackbar({
                    props: {
                        message: $msg('customer.brief.error.unknown'),
                        level: 'error',
                    },
                });
            } else {
                suggestedSkills.value = data.map((skillValue) => new Skill(skillValue));
            }
        }
    });

    watch(
        [languages, skills],
        () => {
            onFormChange();
        },
        {immediate: true, deep: true},
    );

    init();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(StrategyBriefHubStepLayout, { displaySkipButton: showSkipLink.value }, {
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref($msg)('customer.brief.exec.form.freelancer.skills.title.v2')), 1)
      ]),
      default: _withCtx(() => [
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            (_unref(freelancerSkillsService))
              ? (_openBlock(), _createBlock(SkillsField, {
                  key: 0,
                  initialSkills: _unref(skills),
                  category: category.value,
                  jobTitle: jobTitle.value,
                  freelancerSkillsService: _unref(freelancerSkillsService),
                  onUpdate: handleUpdateSkill
                }, null, 8, ["initialSkills", "category", "jobTitle", "freelancerSkillsService"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            (_unref(languagesService))
              ? (_openBlock(), _createBlock(LanguageField, {
                  key: 0,
                  modelValue: languages.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((languages).value = $event)),
                  "languages-service": _unref(languagesService)
                }, null, 8, ["modelValue", "languages-service"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["displaySkipButton"])
  ]))
}
}

})